import { DeleteFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Input, Select, Spin, Tag } from "antd";
import { useRef, useState } from "react";
import useDiscordTagMessages, {
  useFetchDiscordTagMessages,
} from "./useDiscordTagMessages";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import TagsMsgs from "apps/dots-frontend/types/tags_msgs";
import { DiscordSavedMessageTableData } from "./useDiscordMessages";
const { Option } = Select;

export default function MessageTagSelector({
  setMainAlert,
  message,
  updateTags,
  allMessages,
}: {
  setMainAlert: (type: "error" | "warning", message: string) => void;
  message: DiscordSavedMessageTableData;
  updateTags: (
    tag: { id: number; name: string; color: string } | undefined,
    messageId: string,
    action: "add" | "remove"
  ) => void;
  allMessages?: DiscordSavedMessageTableData[];
}) {
  const inputRef = useRef<Input>(null);
  const [newTagName, setNewTagName] = useState("");
  const {
    data: tags,
    isFetching,
    isError,
    refetch: refetchTags,
  } = useFetchDiscordTagMessages();
  const { createTag, deleteTag, loadingTags, relateTag, removeTag } =
    useDiscordTagMessages(setMainAlert);
  const allMsgTags = tags || [];

  const getTagFromId = (id: number) => {
    return allMsgTags.find((tag) => tag.id === id);
  };

  const onNewTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTagName(event.target.value);
  };

  // function to create a new tag, if there an error or there is a tag with the same name already we show an error or warning respectively
  const submitTag = async (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    const { success } = await createTag(newTagName, allMsgTags);
    if (success) {
      setNewTagName("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      refetchTags();
    }
  };

  // function to add/create a relationship between saved message and a specific tag
  const onSelect = async (value: any) => {
    updateTags(getTagFromId(value), message.discord_id, "add");
    const { success } = await relateTag(value, message.discord_id);
    if (!success) {
      updateTags(getTagFromId(value), message.discord_id, "remove");
    }
  };

  // function to remove/delete relationship between saved message and a specific tag
  const onDeselect = async (value: any) => {
    updateTags(getTagFromId(value), message.discord_id, "remove");
    const { success } = await removeTag(value, message.discord_id);

    if (!success) {
      updateTags(getTagFromId(value), message.discord_id, "add");
    }
  };

  //Custom tag
  function TagRender(props: CustomTagProps) {
    const { onClose, label, value } = props;
    const msgTag = allMsgTags.find((element) => element.id === value);
    return (
      <Tag
        color={msgTag?.color}
        closable
        onClose={onClose}
        key={value}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: 2,
          fontWeight: "bold",
        }}
      >
        <span>{msgTag?.name}</span>
      </Tag>
    );
  }

  function CustomOption({ item }: { item: TagsMsgs }) {
    const [showDelete, setShowDelete] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const onDeleteClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (showDeleteConfirmation) {
        setShowDeleteConfirmation(false);
      } else {
        setShowDeleteConfirmation(true);
      }
    };

    const handleDelete = async () => {
      updateTags(getTagFromId(item.id), message.discord_id, "remove");
      await deleteTag(item.id);
      setShowDeleteConfirmation(false);
      refetchTags();
    };

    //Display an extra warning if there are more messages using the tag about to be deleted
    const getErrorDeleteTagWarningMsg = (tag: string) => {
      const numberOfMessagesWithTag = allMessages
        ?.flatMap((msg) => msg.tags_msgs)
        .map((t) => t.name)
        .filter((t) => t === tag).length;
      if (numberOfMessagesWithTag && numberOfMessagesWithTag > 1) {
        return `FYI Deleting this tag completely will remove the tag on the ${
          numberOfMessagesWithTag - 1
        } other messages that are using this tag.\n\n`;
      }
      return null;
    };

    return (
      <div className="flex flex-col">
        <div
          className="w-full flex justify-between items-center"
          onMouseEnter={() => setShowDelete(true)}
          onMouseLeave={() => setShowDelete(false)}
        >
          <Tag
            style={{
              borderRadius: 5,
              minWidth: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            color={item.color}
          >
            {item.name}
          </Tag>
          {showDelete && (
            <div
              className="text-gray-500 mx-3 hover:text-gray-700 flex items-center"
              onClick={onDeleteClick}
            >
              <DeleteFilled />
            </div>
          )}
        </div>
        {showDeleteConfirmation && (
          <div className="w-full">
            <p className="text-xs my-2">
              {getErrorDeleteTagWarningMsg(item.name)}
              Are you sure you want to delete <b>"{item.name}"</b> tag?
            </p>
            <div className="flex justify-end gap-2 m-1">
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirmation(false);
                }}
              >
                No
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleDelete();
                }}
              >
                Yes
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Select
      mode="multiple"
      value={message.tags_msgs.map((tagItem) => tagItem.id)}
      notFoundContent={isFetching ? <Spin size="small" /> : null}
      tagRender={TagRender}
      showArrow
      style={{ width: "100%" }}
      placeholder="Select tags"
      optionFilterProp="label"
      onSelect={onSelect}
      onDeselect={onDeselect}
      dropdownRender={(menu) => {
        if (isError) {
          return (
            <p className="m-2 text-sm text-center">
              An error occurred! 😔
              <br />
              please contact us
            </p>
          );
        }
        if (loadingTags) {
          return <Spin size="small" />;
        }
        return (
          <div>
            {menu}
            <Divider style={{ margin: "8px 0" }} />
            <div className="flex px-2 flex-col gap-1">
              <Input
                placeholder="New tag name"
                ref={inputRef}
                onChange={onNewTagChange}
                value={newTagName}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={submitTag}
                disabled={!Boolean(newTagName) || loadingTags}
                className="mb-1"
              >
                Create Tag
              </Button>
            </div>
          </div>
        );
      }}
    >
      {allMsgTags.length === 0 ? (
        <Option value={-1} disabled>
          You have not created any tags yet, do it below.
        </Option>
      ) : (
        allMsgTags.map((item) => (
          <Option key={item.id} value={item.id} label={item.name}>
            <CustomOption item={item} />
          </Option>
        ))
      )}
    </Select>
  );
}
